import * as React from 'react';
import * as _ from 'lodash';
import { observer } from 'mobx-react';
import { Routes, useJourneyNavigation } from '../../routes';
import { PageHeadingWidget } from '../../widgets/page/PageHeadingWidget';
import { ProfilePageWidget } from '../../widgets/page/ProfilePageWidget';
import { ButtonType, ButtonWidget } from '../../widgets/buttons/ButtonWidget';
import { TableRow } from '../../widgets/table/TableWidget';
import { useSystem } from '../../hooks/useSystem';
import { styled } from '../../theme';
import { DeveloperInviteModel, DeveloperModel, OrganizationModel } from '@journeyapps-platform/lib-journey-core';
import { DeveloperInviteStatus } from '@journeyapps-platform/types-accounts-hub';
import { SearchableTableWidget } from '../../widgets/table/SearchableTableWidget';

interface ProfileInviteRow extends TableRow {
  invite: DeveloperInviteModel;
}

interface ProfileOrgRow extends TableRow {
  org: OrganizationModel;
}

export function getInviteRoleNames(invite: DeveloperInviteModel) {
  const storeNames = _.chain(invite.options.model.requested_role_ids)
    .map((roleId) => invite.options.banks.roleBank.item(roleId)?.options.role.name)
    .compact()
    .value();

  if (storeNames.length < invite.options.model.requested_role_ids.length) {
    return invite.options.model.metadata.role_names ?? [];
  }

  return storeNames;
}

export const ProfileOrganizationsPage: React.FC = observer(() => {
  const system = useSystem();
  const { developStore, dialogStore, orgStore, profileStore } = system;

  const handler = useJourneyNavigation(Routes.PROFILE_ORGANIZATIONS);

  const { USER_ID } = system.context;
  const [currentUser, setCurrentUser] = React.useState<DeveloperModel | null>(null);

  React.useEffect(() => {
    profileStore.loadInvitations();
    orgStore.loadUserOrgs();
  }, []);

  React.useEffect(() => {
    if (!USER_ID) {
      setCurrentUser(null);
      return;
    }

    developStore.resolve(USER_ID).then((developerModel) => {
      developerModel.loadPolicies();
      setCurrentUser(developerModel);
    });
  }, [USER_ID]);

  React.useEffect(() => {
    profileStore.invitations.items.forEach((invitation) => invitation.loadRoles());
  }, [profileStore.invitations]);

  const pendingInviteRows = profileStore.invitations.items
    .filter((invite) => invite.options.model.status == DeveloperInviteStatus.PENDING)
    .map((invite) => {
      return {
        key: invite.id,
        cells: {
          org: invite.orgName,
          roles: getInviteRoleNames(invite).join(', ')
        },
        invite
      };
    });

  return (
    <ProfilePageWidget activeItemKey={Routes.PROFILE_ORGANIZATIONS}>
      <S.Container>
        <PageHeadingWidget title={'Invitations'} />
        <SearchableTableWidget<ProfileInviteRow>
          columns={[
            {
              display: 'Organization',
              key: 'org',
              accessorSearch: (name) => {
                return name;
              }
            },
            {
              display: 'Roles',
              key: 'roles',
              accessorSearch: (name) => {
                return name;
              }
            },
            {
              display: '',
              key: 'actions',
              shrink: true,
              accessor: (event) => {
                return (
                  <S.ActionRow>
                    <ButtonWidget
                      icon={'ban'}
                      label="Decline"
                      type={ButtonType.TERTIARY}
                      action={async (event2, loading) => {
                        loading(true);
                        await event.row.invite.declineInvite();
                        profileStore.loadInvitations();
                        loading(false);
                      }}
                    />
                    <ButtonWidget
                      icon={'check'}
                      label="Accept"
                      type={ButtonType.SECONDARY}
                      action={async (event2, loading) => {
                        loading(true);
                        const { invite } = event.row;

                        await invite.acceptInvite();
                        profileStore.loadInvitations();

                        // Needed since their permissions are probably updated
                        let permissionsPromise = system.reloadUserPermissions();

                        // Update the list
                        orgStore.loadUserOrgs();
                        if (
                          await dialogStore.confirmDialog({
                            title: 'Invitation accepted',
                            message: `Navigate to ${invite.orgName}?`
                          })
                        ) {
                          await permissionsPromise;
                          handler.navigate(Routes.MANAGE_APPS, { org_id: invite.org_id });
                        }

                        loading(false);
                      }}
                    />
                  </S.ActionRow>
                );
              }
            }
          ]}
          rows={pendingInviteRows}
        />
      </S.Container>
      <S.Container>
        <PageHeadingWidget title={'Organizations'} />
        <SearchableTableWidget<ProfileOrgRow>
          columns={[
            {
              display: 'Organization',
              key: 'org',
              accessorSearch: (name) => {
                return name;
              }
            },
            {
              display: 'Roles',
              key: 'roles',
              accessorSearch: (name) => {
                return name;
              }
            },
            {
              display: '',
              key: 'actions',
              shrink: true,
              accessor: (event) => {
                return (
                  <S.ActionRow>
                    <ButtonWidget
                      icon={'arrow-right'}
                      label=""
                      type={ButtonType.SECONDARY}
                      action={async (event2, loading) => {
                        handler.navigate(Routes.MANAGE_APPS, { org_id: event.row.org.id });
                      }}
                    />
                  </S.ActionRow>
                );
              }
            }
          ]}
          rows={_.chain(orgStore.models)
            .values()
            .map((orgModel) => {
              return {
                key: orgModel.id,
                cells: {
                  org: orgModel.model.label,
                  roles: _.chain(currentUser?.roles.items ?? [])
                    .filter((roleModel) => roleModel.role.options.role.org_id == orgModel.id)
                    .map((roleModel) => roleModel.role.options.role.name)
                    .sort()
                    .join(', ')
                    .value()
                },
                org: orgModel
              };
            })
            .sortBy((row) => row.cells.org)
            .value()}
        />
      </S.Container>
    </ProfilePageWidget>
  );
});

namespace S {
  export const Container = styled.div`
    margin: auto;
    margin-bottom: 20px;
    width: 900px;
  `;

  export const ActionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: end;
    padding-right: 40px;
    min-width: 300px;
    column-gap: 5px;
  `;
}
